import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import type { ClickAddToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { PromoChip } from '#src/atoms/PromoChip/index.tsx';
import { AddToCartOnClickWrapper } from '#src/components/cart/AddToCartOnClickWrapper/index.tsx';

import type { AddToCart_anyCard } from './__generated__/index.graphql.ts';

type Props = Omit<ButtonProps, 'onClick' | 'loading'> & {
  anyCard: AddToCart_anyCard;
  origin: ClickAddToCart_Origin;
  onSuccess?: () => void;
  disablePopin?: boolean;
};

const Relative = styled(Vertical)`
  position: relative;
`;

const TopRight = styled(Vertical)`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  right: calc(-1 * var(--half-unit));
`;

const StyledLoadingButton = styled(Button)`
  min-width: var(--quadruple-unit);
`;

const ConditionalTooltip = ({ children }: { children: ReactNode }) => (
  <Tooltip
    placement="top"
    title={
      <FormattedMessage defaultMessage="This item is already in your cart" />
    }
  >
    {children}
  </Tooltip>
);

export const AddToCart = ({
  anyCard,
  origin,
  children,
  disablePopin = false,
  onSuccess,
  ...rest
}: Props) => {
  return (
    <AddToCartOnClickWrapper
      anyCard={anyCard}
      origin={origin}
      onSuccess={onSuccess}
      disablePopin={disablePopin}
    >
      {({ onClick, loading, inCart, isEligibleForCartPromo }) => (
        <ConditionalWrapper wrap={!!isEligibleForCartPromo} Wrapper={Relative}>
          <ConditionalWrapper wrap={inCart} Wrapper={ConditionalTooltip}>
            <StyledLoadingButton
              loading={loading}
              disabled={inCart}
              onClick={onClick}
              {...rest}
            >
              {children || <FontAwesomeIcon icon={iconCart} size="sm" />}
            </StyledLoadingButton>
            {!!isEligibleForCartPromo && (
              <TopRight>
                <PromoChip />
              </TopRight>
            )}
          </ConditionalWrapper>
        </ConditionalWrapper>
      )}
    </AddToCartOnClickWrapper>
  );
};

AddToCart.fragments = {
  anyCard: gql`
    fragment AddToCart_anyCard on AnyCardInterface {
      slug
      ...AddToCartOnClickWrapper_anyCard
    }

    ${AddToCartOnClickWrapper.fragments.anyCard}
  ` as TypedDocumentNode<AddToCart_anyCard>,
};
